import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Link,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import StyleIcon from '@mui/icons-material/Style';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import baseTheme from '../theme';
import { trackWebsiteClick, type WebsiteClickSection } from '../utils/websiteAnalytics';

const ROWDIES_FONT = '"Rowdies", cursive';

const homeTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: ROWDIES_FONT,
    allVariants: {
      fontFamily: ROWDIES_FONT,
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: ROWDIES_FONT },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { fontFamily: ROWDIES_FONT },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { fontFamily: ROWDIES_FONT },
      },
    },
  },
});

// Static assets in public/assets/ — copied to build root on deploy
const ASSETS = {
  logoIcon: '/assets/logo.png',
  logoGroup3: '/assets/group3.png',
  logoFull: '/assets/logo-full.png',
  screenStart: '/assets/screen-start.png',
  screenGame: '/assets/screen-game-blue.png',
  screenGame1: '/assets/screen-game-red.png',
  screenDecks: '/assets/screen-decks.png',
  badgeAppStore: '/assets/badge-app-store.svg',
  badgeGooglePlay: '/assets/badge-google-play.png',
} as const;

const DOWNLOAD_LINK = 'https://onelink.to/4uu6nu';
const APP_STORE_LINK =
  'https://apps.apple.com/tr/app/taboorush-yasaklı-kelimeler/id6758069694';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.celiluysal.taboo';

const LOGO_ICON = ASSETS.logoIcon;
const LOGO_GROUP3 = ASSETS.logoGroup3;
const LOGO_FULL = ASSETS.logoFull;
const SCREEN_GAME = ASSETS.screenGame;
const SCREEN_GAME1 = ASSETS.screenGame1;
const BADGE_APP_STORE = ASSETS.badgeAppStore;
const BADGE_GOOGLE_PLAY = ASSETS.badgeGooglePlay;

const COLORS = {
  purpleDeep: '#1A052D',
  purple: '#2E0854',
  purpleMid: '#4A1488',
  purpleBright: '#8B44D3',
  blue: '#5DA9E9',
  coral: '#E56B6F',
  green: '#48B887',
  yellow: '#FFB300',
  orange: '#FF8C00',
  cyan: '#00F2FF',
  white: '#FFFFFF',
};

const keyframes = {
  float: {
    '0%, 100%': { transform: 'translateY(0px) rotate(-2deg)' },
    '50%': { transform: 'translateY(-18px) rotate(2deg)' },
  },
  floatReverse: {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-12px)' },
  },
  pulse: {
    '0%, 100%': { boxShadow: `0 0 20px ${COLORS.green}66, 0 8px 32px rgba(0,0,0,0.4)` },
    '50%': { boxShadow: `0 0 40px ${COLORS.green}aa, 0 8px 32px rgba(0,0,0,0.4)` },
  },
  spinSlow: {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  fadeInUp: {
    '0%': { opacity: 0, transform: 'translateY(40px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  sparkle: {
    '0%, 100%': { opacity: 0.2, transform: 'scale(0.8)' },
    '50%': { opacity: 1, transform: 'scale(1.2)' },
  },
};

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const LOGO_MORPH_SCROLL = 200;

const morphHeroOpacity = (p: number) => clamp01(1 - p / 0.18);
const morphFloatingOpacity = (p: number) => {
  if (p <= 0.02) return 0;
  if (p >= 0.9) return clamp01(1 - (p - 0.9) / 0.1);
  return 1;
};
const morphHeaderLogoOpacity = (p: number) => clamp01((p - 0.85) / 0.12);
const morphNavOpacity = (p: number) => clamp01((p - 0.45) / 0.4);

// Google Play PNG has transparent vertical padding (192px content in 250px canvas)
const GOOGLE_PLAY_VISUAL_RATIO = 192 / 250;

const StoreBadges: React.FC<{ size?: 'sm' | 'md'; section: WebsiteClickSection }> = ({
  size = 'md',
  section,
}) => {
  const visualHeight = size === 'sm' ? 40 : 48;

  const badges = [
    {
      src: BADGE_APP_STORE,
      alt: "App Store'dan indirin",
      label: "App Store'dan indirin",
      href: APP_STORE_LINK,
      height: visualHeight,
      button: 'app_store' as const,
    },
    {
      src: BADGE_GOOGLE_PLAY,
      alt: "Google Play'den edinin",
      label: "Google Play'den edinin",
      href: GOOGLE_PLAY_LINK,
      height: Math.round(visualHeight / GOOGLE_PLAY_VISUAL_RATIO),
      button: 'google_play' as const,
    },
  ] as const;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
      {badges.map((badge) => (
        <Box
          key={badge.label}
          component="a"
          href={badge.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={badge.label}
          onClick={() => trackWebsiteClick(badge.button, section)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            lineHeight: 0,
            transition: 'transform 0.2s ease, opacity 0.2s ease',
            '&:hover': { transform: 'scale(1.04)', opacity: 0.92 },
          }}
        >
          <Box
            component="img"
            src={badge.src}
            alt={badge.alt}
            sx={{
              height: badge.height,
              width: 'auto',
              display: 'block',
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

const features = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
    title: 'Modern ve Eğlenceli Deneyim',
    description:
      'Sade tasarım, akıcı oynanış ve hızlı oyun akışıyla arkadaşların ve ailenle eğlenceye hemen başla.',
    color: COLORS.cyan,
  },
  {
    icon: <StyleIcon sx={{ fontSize: 40 }} />,
    title: '2.500+ Kelime Kartı',
    description:
      "15.000'den fazla kelime içeren binlerce kart sayesinde her oyun farklı bir deneyim sunar. Yeni içerikler düzenli olarak eklenir.",
    color: COLORS.coral,
  },
  {
    icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
    title: 'Ücretsiz Oyna veya Premium’a Geç',
    description:
      'İlk 200 kelimeyi ücretsiz keşfet. Daha fazla destenin kilidini reklam izleyerek açabilir veya tek seferlik Premium ile tüm mevcut ve gelecekte eklenecek içeriklere reklamsız erişebilirsin.',
    color: COLORS.green,
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    title: 'Takımını Kur, Yarışmaya Başla',
    description:
      'İki takıma ayrıl, süre dolmadan yasaklı kelimeleri kullanmadan anlat ve en yüksek puanı toplayarak oyunu kazan.',
    color: COLORS.blue,
  },
];

const screenshots = [
  { src: ASSETS.screenStart, alt: 'Ana menü' },
  { src: SCREEN_GAME, alt: 'Oyun ekranı' },
  { src: ASSETS.screenDecks, alt: 'Deste seçimi' },
  { src: SCREEN_GAME1, alt: 'Oyun modu' },
];

const Sparkle: React.FC<{ top: string; left: string; delay: number; size?: number }> = ({
  top,
  left,
  delay,
  size = 6,
}) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      width: size,
      height: size,
      borderRadius: '50%',
      background: COLORS.white,
      animation: `sparkle 2.5s ease-in-out ${delay}s infinite`,
      '@keyframes sparkle': keyframes.sparkle,
      pointerEvents: 'none',
    }}
  />
);

const PHONE_MOCKUP_WIDTH = {
  md: { xs: 220, sm: 260, md: 280 },
  sm: { xs: 160, sm: 200, md: 280 },
} as const;

const PhoneMockup: React.FC<{
  src: string;
  delay?: number;
  rotate?: number;
  size?: keyof typeof PHONE_MOCKUP_WIDTH;
}> = ({
  src,
  delay = 0,
  rotate = -4,
  size = 'md',
}) => (
  <Box
    sx={{
      pt: { xs: '12px', md: '16px' },
      width: PHONE_MOCKUP_WIDTH[size],
      mx: 'auto',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        animation: `floatReverse 4s ease-in-out ${delay}s infinite, fadeInUp 0.8s ease-out ${delay}s both`,
        '@keyframes floatReverse': keyframes.floatReverse,
        '@keyframes fadeInUp': keyframes.fadeInUp,
        transform: `rotate(${rotate}deg)`,
      }}
    >
    <Box
      sx={{
        borderRadius: '36px',
        p: '10px',
        background: 'linear-gradient(145deg, #3a3a3a 0%, #1a1a1a 50%, #2a2a2a 100%)',
        boxShadow: '0 25px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)',
      }}
    >
      <Box
        sx={{
          borderRadius: '28px',
          overflow: 'hidden',
          position: 'relative',
          aspectRatio: '9/19.5',
        }}
      >
        <Box
          component="img"
          src={src}
          alt="TabuRush screenshot"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    </Box>
    <Box
      sx={{
        position: 'absolute',
        top: '3.5%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '28%',
        aspectRatio: '40 / 11',
        borderRadius: '999px',
        background: '#111',
        zIndex: 2,
      }}
    />
    </Box>
  </Box>
);

const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  index: number;
}> = ({ icon, title, description, color, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'flex-start', md: 'stretch' },
        gap: { xs: 1.5, md: 0 },
        p: { xs: 1.5, sm: 2.5, md: 3 },
        borderRadius: { xs: '18px', md: '24px' },
        background: `linear-gradient(135deg, ${color}22 0%, rgba(255,255,255,0.05) 100%)`,
        border: `2px solid ${color}44`,
        backdropFilter: 'blur(12px)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, opacity 0.5s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${index * 0.1}s`,
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px ${color}33`,
          borderColor: color,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 36, md: 64 },
          height: { xs: 36, md: 64 },
          borderRadius: { xs: '10px', md: '18px' },
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: COLORS.white,
          mb: { xs: 0, md: 2 },
          boxShadow: `0 8px 20px ${color}55`,
          flexShrink: 0,
          '& svg': { fontSize: { xs: 20, md: 40 } },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.white,
            fontWeight: 800,
            mb: { xs: 0.5, md: 1 },
            fontSize: { xs: '0.95rem', md: '1.25rem' },
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            flex: { md: 1 },
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.55,
            fontSize: { xs: '0.8rem', md: '0.95rem' },
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const Home: React.FC = () => {
  const heroLogoRef = useRef<HTMLDivElement>(null);
  const headerLogoRef = useRef<HTMLDivElement>(null);
  const heroAnchorRef = useRef({ top: 0, left: 0, width: 0, height: 0 });
  const [logoMorph, setLogoMorph] = useState(0);
  const [morphStyle, setMorphStyle] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const captureHeroAnchor = () => {
    const heroEl = heroLogoRef.current;
    if (!heroEl) return;
    const rect = heroEl.getBoundingClientRect();
    heroAnchorRef.current = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  };

  const updateLogoMorph = () => {
    const scrollY = window.scrollY;
    const raw = Math.min(1, Math.max(0, scrollY / LOGO_MORPH_SCROLL));
    const progress = easeInOutCubic(raw);

    setLogoMorph(progress);

    const headerEl = headerLogoRef.current;
    const heroEl = heroLogoRef.current;
    if (!headerEl) return;

    if (scrollY === 0) {
      captureHeroAnchor();
    }

    const heroStart = heroAnchorRef.current;
    const heroLive = heroEl?.getBoundingClientRect();
    const header = headerEl.getBoundingClientRect();

    const startTop = progress < 0.02 && heroLive ? heroLive.top : heroStart.top - scrollY;
    const startLeft = progress < 0.02 && heroLive ? heroLive.left : heroStart.left;
    const startWidth = heroStart.width || heroLive?.width || 0;
    const startHeight = heroStart.height || heroLive?.height || 0;

    // Vertical settles into header row quickly; horizontal + scale follow full scroll
    const verticalT = easeOutCubic(Math.min(1, progress / 0.4));

    setMorphStyle({
      top: lerp(startTop, header.top, verticalT),
      left: lerp(startLeft, header.left, progress),
      width: lerp(startWidth, header.width, progress),
      height: lerp(startHeight, header.height, progress),
    });
  };

  useEffect(() => {
    const init = () => {
      captureHeroAnchor();
      updateLogoMorph();
    };
    init();
    window.addEventListener('scroll', updateLogoMorph, { passive: true });
    window.addEventListener('resize', init);
    return () => {
      window.removeEventListener('scroll', updateLogoMorph);
      window.removeEventListener('resize', init);
    };
  }, []);

  const navOpacity = morphNavOpacity(logoMorph);
  const floatingOpacity = morphFloatingOpacity(logoMorph);
  const headerLogoOpacity = morphHeaderLogoOpacity(logoMorph);
  const headerInteractive = navOpacity > 0.35;

  return (
    <ThemeProvider theme={homeTheme}>
    <Box
      sx={{
        minHeight: '100vh',
        fontFamily: ROWDIES_FONT,
        background: `linear-gradient(180deg, ${COLORS.purpleMid} 0%, ${COLORS.purpleDeep} 40%, #0D0118 100%)`,
        color: COLORS.white,
        overflowX: 'hidden',
      }}
    >
      {/* Morphing logo — hero → header */}
      {morphStyle && floatingOpacity > 0 && (
        <Box
          component="img"
          src={LOGO_GROUP3}
          alt="TabuRush"
          sx={{
            position: 'fixed',
            top: morphStyle.top,
            left: morphStyle.left,
            width: morphStyle.width,
            height: morphStyle.height,
            objectFit: 'contain',
            opacity: floatingOpacity,
            zIndex: 110,
            pointerEvents: 'none',
            willChange: 'top, left, width, height, opacity',
          }}
        />
      )}

      {/* Nav — fades in as logo approaches header */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          opacity: navOpacity,
          pointerEvents: headerInteractive ? 'auto' : 'none',
          background: `rgba(26, 5, 45, ${navOpacity * 0.92})`,
          backdropFilter: navOpacity > 0.2 ? 'blur(16px)' : 'none',
          borderBottom: navOpacity > 0.2 ? `1px solid rgba(255,255,255,${navOpacity * 0.08})` : 'none',
        }}
      >
        <Box
          ref={headerLogoRef}
          sx={{
            width: { xs: 120, sm: 140 },
            height: { xs: 36, sm: 42 },
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={LOGO_GROUP3}
            alt="TabuRush"
            sx={{
              display: 'block',
              height: '100%',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              opacity: headerLogoOpacity,
            }}
          />
        </Box>
        <Button
          component="a"
          href={DOWNLOAD_LINK}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          onClick={() => trackWebsiteClick('download_nav', 'header')}
          sx={{
            background: `linear-gradient(135deg, ${COLORS.green}, #3a9e72)`,
            borderRadius: '50px',
            px: 3,
            fontWeight: 800,
            textTransform: 'none',
            fontSize: '0.95rem',
            opacity: navOpacity,
            visibility: navOpacity > 0.05 ? 'visible' : 'hidden',
            animation: navOpacity > 0.85 ? 'pulse 2.5s ease-in-out infinite' : 'none',
            '@keyframes pulse': keyframes.pulse,
            '&:hover': { background: `linear-gradient(135deg, #55c99a, ${COLORS.green})` },
          }}
        >
          İndir
        </Button>
      </Box>

      {/* Hero */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '100vh' },
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          pt: { xs: 2, sm: 3, md: 6 },
          pb: { xs: 4, md: 6 },
          overflow: 'hidden',
        }}
      >
        {/* Sunburst */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: { xs: 600, md: 900 },
            height: { xs: 600, md: 900 },
            transform: 'translate(-50%, -50%)',
            background: `conic-gradient(from 0deg, transparent 0deg, ${COLORS.purpleBright}11 10deg, transparent 20deg)`,
            backgroundSize: '100% 100%',
            animation: 'spinSlow 60s linear infinite',
            '@keyframes spinSlow': keyframes.spinSlow,
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        />

        <Sparkle top="15%" left="10%" delay={0} size={8} />
        <Sparkle top="25%" left="85%" delay={0.5} />
        <Sparkle top="70%" left="5%" delay={1.2} size={5} />
        <Sparkle top="60%" left="92%" delay={0.8} size={7} />
        <Sparkle top="40%" left="50%" delay={1.5} size={4} />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  animation: 'fadeInUp 0.8s ease-out both',
                  textAlign: 'center',
                  '@keyframes fadeInUp': keyframes.fadeInUp,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: COLORS.cyan,
                    fontWeight: 700,
                    letterSpacing: 3,
                    fontSize: '0.85rem',
                    display: 'block',
                    mb: { xs: 1, md: 2 },
                  }}
                >
                  YASAKLI KELİME OYUNU
                </Typography>

                <Box
                  ref={heroLogoRef}
                  sx={{
                    width: { xs: 'min(100%, 280px)', sm: 380, md: 440 },
                    mx: 'auto',
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  <Box
                    component="img"
                    src={LOGO_GROUP3}
                    alt="TabuRush"
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      opacity: morphHeroOpacity(logoMorph),
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: 1.7,
                    mb: { xs: 3, md: 4 },
                    maxWidth: 560,
                    mx: 'auto',
                  }}
                >
                  2.500+ kelime kartı, modern tasarım ve akıcı oynanış. En eğlenceli yasaklı kelime
                  oyunu seni bekliyor!
                </Typography>

                <Box sx={{ mb: { xs: 1.5, md: 3 }, display: 'flex', justifyContent: 'center' }}>
                  <StoreBadges section="hero" />
                </Box>

              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <PhoneMockup src={SCREEN_GAME} delay={0.2} rotate={-3} size="sm" />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Box component="section" sx={{ py: { xs: 5, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            textAlign="center"
            sx={{
              fontSize: { xs: '1.65rem', md: '2.8rem' },
              fontWeight: 900,
              mb: 1,
            }}
          >
            Neden{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(90deg, ${COLORS.yellow}, ${COLORS.cyan})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              TabuRush?
            </Box>
          </Typography>
          <Typography
            textAlign="center"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              mb: { xs: 3, md: 6 },
              fontSize: { xs: '0.9rem', md: '1.1rem' },
            }}
          >
            Partilerin vazgeçilmezi, kelime oyununun en eğlenceli hali
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} alignItems="stretch">
            {features.map((f, i) => (
              <Grid item xs={12} sm={6} md={6} key={f.title} sx={{ display: 'flex' }}>
                <FeatureCard {...f} index={i} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Screenshots */}
      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            textAlign="center"
            sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 900, mb: 2 }}
          >
            Oyunu Keşfet
          </Typography>
          <Typography
            textAlign="center"
            sx={{ color: 'rgba(255,255,255,0.6)', mb: 6 }}
          >
            Canlı renkler, akıcı arayüz, bağımlılık yapan oynanış
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              pb: 3,
              px: 1,
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                background: COLORS.purpleBright,
                borderRadius: 3,
              },
            }}
          >
            {screenshots.map((shot, i) => (
              <Box
                key={`${shot.src}-${i}`}
                sx={{
                  flex: '0 0 auto',
                  scrollSnapAlign: 'center',
                  animation: `fadeInUp 0.6s ease-out ${i * 0.12}s both`,
                  '@keyframes fadeInUp': keyframes.fadeInUp,
                }}
              >
                <PhoneMockup src={shot.src} delay={i * 0.3} rotate={i % 2 === 0 ? -2 : 2} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        component="section"
        id="download"
        sx={{
          py: { xs: 10, md: 14 },
          position: 'relative',
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src={LOGO_FULL}
          alt="TabuRush logo"
          sx={{
            display: 'block',
            width: { xs: 280, md: 400 },
            mx: 'auto',
            mb: 4,
            animation: 'float 4s ease-in-out infinite',
            '@keyframes float': keyframes.float,
            filter: 'drop-shadow(0 20px 40px rgba(139,68,211,0.5))',
          }}
        />

        <Typography
          sx={{
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            fontWeight: 900,
            mb: 2,
            px: 2,
          }}
        >
          Hemen Oynamaya Başla!
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.65)', mb: 5, fontSize: '1.1rem', px: 2 }}
        >
          Ücretsiz indir, arkadaşlarını davet et, eğlence başlasın.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
          <StoreBadges size="md" section="cta" />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 3,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(0,0,0,0.3)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={LOGO_ICON}
                alt="TabuRush"
                sx={{ display: 'block', height: 32, width: 32 }}
              />
              <Typography sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                © {new Date().getFullYear()} TabuRush. Tüm hakları saklıdır.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link
                href="/privacy"
                underline="hover"
                onClick={() => trackWebsiteClick('privacy_policy', 'footer')}
                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}
              >
                Gizlilik Politikası
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default Home;
